import { inStage, putToPresignedUrl, unwrap } from "@sagot/api-client";
import { contentTypeFor } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogClose, DialogContent, FileDropzone, IngestionRow, type IngestionItem } from "@sagot/ui";
import { useState } from "react";
import { useUploadErrorMessage } from "../../../lib/upload-errors";
import { useApi } from "../../../session/session-provider";
import type { SourceView } from "../shared/types";
import { useInvalidateSource } from "./use-source-detail";

interface ReplaceFileDialogProps {
  source: SourceView;
  onOpenChange: (open: boolean) => void;
}

function useDropzoneLabels() {
  const { t } = useTranslation();
  const k = "deployKnowledge.sourceDetail.replace";
  return {
    title: t(`${k}.dropTitle`),
    hint: t(`${k}.dropHint`),
    dragActive: t(`${k}.dragActive`),
    rejected: ({ file, reason }: { file: File; reason: string }) => t(`${k}.rejected.${reason}`, { name: file.name }),
  };
}

/** New version: presign (`POST /v1/sources/uploads` + source_id) → PUT with progress → finalize. */
export function ReplaceFileDialog({ source, onOpenChange }: ReplaceFileDialogProps) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useUploadErrorMessage();
  const invalidate = useInvalidateSource(source.id);
  const labels = useDropzoneLabels();
  const [item, setItem] = useState<IngestionItem | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const k = "deployKnowledge.sourceDetail.replace";
  const busy = item !== null && item.stage === "uploading";

  const upload = async (file: File) => {
    setNote(null);
    setItem({ id: file.name, name: file.name, stage: "uploading", progress: 0 });
    try {
      const ticket = await inStage("presign", () =>
        unwrap(api.POST("/v1/sources/uploads", { body: { source_id: source.id, filename: file.name, content_type: contentTypeFor(file), size_bytes: file.size, ocr_mode: "auto" } })),
      );
      const target = { url: ticket.upload_url, method: ticket.method, headers: ticket.headers };
      await putToPresignedUrl(target, file, { onProgress: (progress) => { setItem((prev) => (prev ? { ...prev, progress } : prev)); } });
      const result = await inStage("finalize", () => unwrap(api.POST("/v1/sources/{source_id}/finalize", { params: { path: { source_id: source.id } } })));
      if (result.outcome === "infected") {
        setItem({ id: file.name, name: file.name, stage: "failed", error: t(`${k}.infected`) });
        return;
      }
      setItem({ id: file.name, name: file.name, stage: result.outcome === "queued" ? "parsing" : "ready", progress: 0 });
      setNote(result.outcome === "queued" ? t(`${k}.queued`, { version: result.version }) : t(`${k}.unchanged`));
      await invalidate();
    } catch (error) {
      setItem({ id: file.name, name: file.name, stage: "failed", error: message(error, t(`${k}.failed`)) });
    }
  };

  const stageLabels = {
    uploading: t("deployKnowledge.sourceDetail.stages.uploading"),
    parsing: t("deployKnowledge.sourceDetail.stages.parsing"),
    ready: t("deployKnowledge.sourceDetail.stages.ready"),
    failed: t("deployKnowledge.sourceDetail.stages.failed"),
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!busy) onOpenChange(open); }}>
      <DialogContent
        title={t(`${k}.title`)}
        closeLabel={t("common.close")}
        footer={
          <DialogClose asChild>
            <Button variant="ghost" disabled={busy}>{t(`${k}.close`)}</Button>
          </DialogClose>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-fg-muted">{t(`${k}.body`, { next: source.version + 1, current: source.version })}</p>
          <FileDropzone multiple={false} disabled={busy} labels={labels} onFiles={(files) => { const file = files[0]; if (file) void upload(file); }} />
          {item ? (
            <ul aria-live="polite">
              <IngestionRow item={item} labels={stageLabels} />
            </ul>
          ) : null}
          {note ? <p className="text-sm text-fg-muted" role="status">{note}</p> : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
